/** @jsxImportSource @emotion/react */
import React, { useState, useImperativeHandle, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { addClass } from '@grapecity/wijmo';
import { CellType } from '@grapecity/wijmo.grid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import CustomGrid from 'components/grids/CustomGrid';
import { getExcelFileName } from 'utils/ExcelUtil';
import { DefaultZeroNumberCellTemplate } from 'components/grids/NumberCellRenderer';

interface Props {
  machineRowData?: any;
}

/**
 * @desc 결과보기 > [기계요약] 탭 내용
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.10.02   jshwang12       자연수(ex. 수량)를 제외한 숫자는 소수점 1자리까지 표현(2자리에서 반올림 처리)
 * 2026.10.13   jihwan89        CSR E20260921-000266 수용율, Actual Load 값 추가
 *                              수용율 변경에 따라 Total * 수용율 = Actual Load 저장되진않음.
 *                              수용율 기본값은 100
 */
const MachineSummaryContent = ({ machineRowData }: Props, ref) => {
  const [flexRef, setFlexRef] = useState<any>();
  const [gridRowData, setGridRowData] = useState<any[]>([]);

  const { t } = useTranslation();

  useEffect(() => {
    if (!machineRowData?.list || !machineRowData?.col) return;

    const rowData = [...machineRowData.list];

    // Total Sum 행 찾기
    const totalSumIdx = rowData.findIndex((r: any) => r['PRDN_PROC_NM'] === 'Total Sum');

    // 수용율 행 생성 (기본값 100)
    const dmdRateRow: any = {
      PRDN_PROC_NM: '수용율',
      EQCL_ID_NM: '',
      EQCL_ID: '',
    };
    // Actual Load 행 생성
    const actualLoadRow: any = {
      PRDN_PROC_NM: 'Actual Load',
      EQCL_ID_NM: '',
      EQCL_ID: '',
    };

    // 각 UTM 컬럼에 대해 수용율=100, Actual Load=Total Sum × 100/100
    const prevHeaderNames = [
      '_CDA',
      '_N2',
      '_N2_AVG',
      '_IW',
      '_IW2',
      '_PCW',
      '_WW',
      '_STEAM',
      '_NG',
      '_EX_RETURN',
      '_EX_VENT',
      '_VP',
      '_SA',
    ];
    machineRowData.col.forEach((colItem: any) => {
      const utmKey = colItem.KEY;
      prevHeaderNames.forEach((suffix) => {
        const binding = `'${utmKey}'${suffix}`;
        dmdRateRow[binding] = 100;
        if (totalSumIdx !== -1) {
          const totalVal = parseFloat(rowData[totalSumIdx][binding]) || 0;
          actualLoadRow[binding] = totalVal;
        } else {
          actualLoadRow[binding] = 0;
        }
      });
    });

    // Total Sum 행 다음에 수용율, Actual Load 행 삽입
    if (totalSumIdx !== -1) {
      rowData.splice(totalSumIdx + 1, 0, dmdRateRow, actualLoadRow);
    } else {
      rowData.push(dmdRateRow, actualLoadRow);
    }

    setGridRowData(rowData);
  }, [machineRowData]);

  useImperativeHandle(ref, () => ({
    btnMachineExcelExport: () => {
      btnMachineExcelExport();
    },
  }));

  const flexLayoutDefinition = useMemo(() => {
    const prevHeaderNames = [
      '_LINE_QTY',
      '_EQP_QTY',
      '_CDA',
      '_N2',
      '_N2_AVG',
      '_IW',
      '_IW2',
      '_PCW',
      '_WW',
      '_STEAM',
      '_NG',
      '_EX_RETURN',
      '_EX_VENT',
      // 26.08.05 CSR E20260727-000228 Vacuum Pump 추가
      '_VP',
      '_SA',
    ];

    const parentHeaderNames = [
      String(t('ut.label.라인', '라인')),
      String(t('ut.label.설비', '설비')),
      String(t('ut.label.CDA[l/min]', 'CDA[Nl/min]')),
      String(t('ut.label.N2 Peak [l/min]', 'N2 Peak [Nl/min]')),
      String(t('ut.label.N2 AVG [l/min]', 'N2 AVG [Nl/min]')),
      String(t('ut.label.IW[l/min]', 'IW[ℓ/min]')),
      String(t('ut.label.IW2[l/min]', 'FW[ℓ/min]')),
      String(t('ut.label.PCW[l/min]', 'PCW[ℓ/min]')),
      String(t('ut.label.WW[l/min]', 'WW[ℓ/min]')),
      String(t('ut.label.Steam[Ton/hr]', 'Steam[t/hr]')),
      String(t('ut.label.NG[N㎥/hr]', 'NG[N㎥/hr]')),
      String(t('ut.label.Return air[CMH]', 'Return air[CMH]')),
      String(t('ut.label.Exhaust air[CMH]', 'Exhaust air[CMH]')),
      // 26.08.05 CSR E20260727-000228 Vacuum Pump 추가
      String(t('ut.label.Vacuum Pump[CMH]', 'Vacuum Pump[CMH]')),
      String(t('ut.label.Supply Air[CMH]', 'Supply air[CMH]')),
    ];

    const flexLayout = [] as any[];

    if (machineRowData !== undefined) {
      const childHeader: string[] = [];

      if (machineRowData?.col.length > 0) {
        for (let i = 0; i < prevHeaderNames.length; i++) {
          for (let j = 0; j < machineRowData.col.length; j++) {
            const data = "'" + machineRowData.col[j].KEY + "'" + prevHeaderNames[i];
            childHeader.push(data);
          }
        }
      }

      const result = {};

      for (let i = 0; i < childHeader.length; i += machineRowData.col.length) {
        const key = Math.floor(i / machineRowData.col.length);
        result[key] = childHeader.slice(i, i + machineRowData.col.length);
      }

      let prevHeaderName = '';
      let prevHeaderIdx = 0;
      if (parentHeaderNames.length > 0) {
        parentHeaderNames.forEach((item, index) => {
          // Header 추가
          if (prevHeaderName !== item) {
            prevHeaderIdx = index;
            prevHeaderName = item;
            flexLayout.push({ header: item, align: 'center', columns: [] });
          }
          machineRowData.col.forEach((item, i) => {
            const isLineQty = parentHeaderNames[index] === String(t('ut.label.라인', '라인'));
            const isEqpQty = parentHeaderNames[index] === String(t('ut.label.설비', '설비'));
            flexLayout[flexLayout.length - 1]['columns'].push({
              header: i + 1,
              align: 'right',
              width: '*',
              minWidth: 150,
              binding: result[index][i],
              isReadOnly: isLineQty || isEqpQty ? true : false,
              cellTemplate: isLineQty || isEqpQty ? undefined : DefaultZeroNumberCellTemplate,
            });
          });
        });
      }
    }

    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        width: 40,
        align: 'center',
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        binding: 'PRDN_PROC_NM',
        header: String(t('ut.label.공정', '공정')),
        width: 100,
        isReadOnly: true,
      },
      {
        binding: 'EQCL_ID_NM',
        header: String(t('ut.label.설비군', '설비군')),
        width: '*',
        minWidth: 150,
        isReadOnly: true,
        cellTemplate: (params) =>
          `${params.item['PRDN_PROC_NM'] === 'Total Sum' ? 'Total Sum' : params.value}`,
      },
      {
        binding: 'EQCL_ID',
        visible: false,
      },
      ...flexLayout,
    ];
  }, [machineRowData]);

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;
      if (binding) {
        if (item['PRDN_PROC_NM'] === 'Total Sum') {
          addClass(cell, 'sumTotal');
        } else if (item['EQCL_ID_NM'] === 'Sub Sum') {
          addClass(cell, 'rowTotal');
        } else if (item['PRDN_PROC_NM'] === '수용율') {
          addClass(cell, 'demandRate');
        } else if (item['PRDN_PROC_NM'] === 'Actual Load') {
          addClass(cell, 'actualLoad');
        }
      }
      if (item['PRDN_PROC_NM'] !== '수용율') {
        cell.ariaReadOnly = true;
      }
    }
  });

  const onCellEditEnding = useEvent((grid, e) => {
    const item = grid.rows[e.row].dataItem;
    const binding = grid.columns[e.col].binding;

    // 수용율 행이 수정된 경우
    if (item['PRDN_PROC_NM'] === '수용율' && binding && binding.includes("'")) {
      const newRate = parseFloat(grid.activeEditor?.value) || 0;

      // Total Sum 행 찾기
      const totalSumRow = grid.rows.findIndex((r) => r.dataItem['PRDN_PROC_NM'] === 'Total Sum');
      if (totalSumRow !== -1) {
        const totalSumItem = grid.rows[totalSumRow].dataItem;

        // Actual Load 행 찾기
        const actualLoadRow = grid.rows.findIndex(
          (r) => r.dataItem['PRDN_PROC_NM'] === 'Actual Load'
        );
        if (actualLoadRow !== -1) {
          const actualLoadItem = grid.rows[actualLoadRow].dataItem;

          // 수정된 컬럼만 Actual Load 재계산
          const totalSumValue = parseFloat(totalSumItem[binding]) || 0;
          const actualLoadValue = (totalSumValue * newRate) / 100;
          actualLoadItem[binding] = actualLoadValue;

          // 그리드 업데이트 (setTimeout으로 재귀 호출 방지)
          setTimeout(() => {
            grid.refresh();
          }, 0);
        }
      }
    }
  });

  const onInitialized = (grid) => {
    setFlexRef(grid);
    grid.itemFormatter = onItemFormatter;
  };

  const btnMachineExcelExport = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(flexRef, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = '기계요약';
    book.saveAsync(getExcelFileName(t('ut.label.기계요약', '기계요약')));
  };

  return (
    <>
      <CustomGrid
        layoutDefinition={flexLayoutDefinition}
        rowData={gridRowData}
        height={gridRowData.length > 0 ? 400 : 400}
        initialized={onInitialized}
        isSelector={false}
        isFilter={false}
        isHoverCss={false}
        allowPinning={false}
        frozenColumns={3}
        beginningEdit={(grid, e) => {
          const item = grid.rows[e.row].dataItem;
          // 수용율 행이 아닌 경우 편집 취소
          if (item['PRDN_PROC_NM'] !== '수용율') {
            e.cancel = true;
          }
        }}
        cellEditEnding={onCellEditEnding}
      />
    </>
  );
};

export default React.forwardRef(MachineSummaryContent);
